import type { ReportsResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BackIcon, CalendarIcon } from '@/components/pulse/icons';
import { PageHeader } from '@/components/pulse/page-header';
import { useReport } from '@/domains/reports/api';

const CHIP: Readonly<Record<string, string>> = { 'On track': 'c-merged', 'At risk': 'c-warn', Late: 'c-changes', Done: 'c-approved', Closed: 'c-kind' };

/** Weekly status (WR-1 to WR-3), built from the prototype's `viewReports()`. For the team only. */
export function ReportsPage(): React.ReactElement {
  const [search] = useSearchParams();
  const page = useReport(search.get('week'));
  const data = page.data;
  return (
    <>
      <PageHeader
        eyebrow={data === undefined ? 'Reports' : data.writtenLabel === null ? 'Reports · not written' : `Reports · written ${data.writtenLabel}`}
        title="Weekly status"
        sub="Talos writes it every Sunday night; Pulse's own figures sit beside it. For the team only."
        right={data !== undefined ? <WeekStepper data={data} /> : undefined}
      />
      {data !== undefined ? <ReportView data={data} /> : null}
    </>
  );
}

export function WeekStepper({ data }: { readonly data: ReportsResponse }): React.ReactElement {
  const step = (week: string | null, label: string, flipped: boolean) =>
    week === null ? (
      <button type="button" className="btn" aria-label={label} disabled title={flipped ? 'This is the latest report' : undefined}>
        <Arrow flipped={flipped} />
      </button>
    ) : (
      <Link className="btn" aria-label={label} to={`/reports?week=${week}`}>
        <Arrow flipped={flipped} />
      </Link>
    );
  return (
    <div className="row" style={{ gap: 6 }} role="group" aria-label="Week">
      {step(data.previousWeek, 'Previous week', false)}
      <span className="datepill">
        <CalendarIcon />
        <span>{data.weekLabel.replace(/^week/, 'Week')}</span>
      </span>
      {step(data.nextWeek, 'Next week', true)}
    </div>
  );
}

function Arrow({ flipped }: { readonly flipped: boolean }): React.ReactElement {
  return flipped ? (
    <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
      <BackIcon />
    </span>
  ) : (
    <BackIcon />
  );
}

export function ReportView({ data }: { readonly data: ReportsResponse }): React.ReactElement {
  return (
    <>
      <section className="card pad stack" style={{ gap: 8 }}>
        <h2>The team</h2>
        <span style={{ color: data.team === null ? 'var(--muted)' : 'var(--ink-2)' }}>{data.team ?? "Talos didn't write this week's report. Pulse's own figures are below."}</span>
      </section>
      {data.projects.map((project) => (
        <section key={project.slug} className="card pad stack" style={{ gap: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
            <h2>{project.name}</h2>
            <Link className="linkish" to={`/projects/${project.slug}`} style={{ fontSize: 13 }}>
              Open project
            </Link>
          </div>
          <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 24, alignItems: 'start' }}>
            <div className="stack" style={{ gap: 10 }}>
              {project.narrative === null ? (
                <span style={{ color: 'var(--muted)' }}>No narrative from Talos for {project.name} this week.</span>
              ) : (
                (
                  [
                    ['What moved', project.narrative.moved],
                    ['At risk', project.narrative.risk],
                    ['Next', project.narrative.next],
                  ] as const
                ).map(([label, text]) => (
                  <div key={label} className="stack" style={{ gap: 2 }}>
                    <span className="eyebrow">{label}</span>
                    <span style={{ color: 'var(--ink-2)' }}>{text}</span>
                  </div>
                ))
              )}
            </div>
            <div className="stack" style={{ gap: 10 }}>
              <div className="row num" style={{ gap: 18, flexWrap: 'wrap' }}>
                {(
                  [
                    ['Built', project.figures.built],
                    ['Merged', project.figures.merged],
                    ['To production', project.figures.toProduction],
                    ['Accepted', project.figures.accepted ?? '—'],
                  ] as const
                ).map(([label, value]) => (
                  <span key={label} className="stack">
                    <b style={{ fontSize: 20 }}>{value}</b>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</span>
                  </span>
                ))}
              </div>
              {project.meetings.length > 0 ? (
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  Meetings:{' '}
                  {project.meetings.map((meeting, index) => (
                    <React.Fragment key={`${meeting.title}${meeting.dateLabel}`}>
                      {index > 0 ? '; ' : ''}
                      <Link to={`/projects/${project.slug}/meetings`}>
                        {meeting.title}, {meeting.dateLabel}
                      </Link>
                    </React.Fragment>
                  ))}
                </span>
              ) : null}
              {project.milestones.map((milestone) => (
                <div key={milestone.title} className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13 }}>
                    {milestone.title} <span style={{ color: 'var(--muted)' }}>· {milestone.dateLabel}</span>
                  </span>
                  <span className={`chip ${CHIP[milestone.state] ?? 'c-kind'}`}>{milestone.state}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </>
  );
}
